import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from '../../services/toastMessage';
import AssistantPromptBox, { AssistantPromptCopy } from './AssistantPromptBox';
import { useSpeechDictation } from '../../hooks/useSpeechDictation';
import { identifyAccessory, draftAccessory } from '../../services/AccessoryAssistService';
import { IAccessoryDraft, IAccessoryIdentifyResult } from '../../interfaces/heavyDutyMachinery/IAccessoryAssist';
import { IClarifyingQuestion, IServiceRequestClarification } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const ACCESSORY_ASSISTANT_COPY: AssistantPromptCopy = {
  titleKey: TRANSLATIONS.ACCESSORY_AI_TITLE,
  subtitleKey: TRANSLATIONS.ACCESSORY_AI_SUBTITLE,
  doneSubtitleKey: TRANSLATIONS.ACCESSORY_AI_DONE_SUBTITLE,
  placeholderKey: TRANSLATIONS.ACCESSORY_AI_PLACEHOLDER,
  fillButtonKey: TRANSLATIONS.MACHINERY_AI_FILL_BUTTON,
  retryButtonKey: TRANSLATIONS.MACHINERY_AI_RETRY_BUTTON,
  continueButtonKey: TRANSLATIONS.MACHINERY_AI_CONTINUE_BUTTON,
};

/** The same floor the server refuses below -- "MB1700" is a complete thing to say. */
const MIN_DESCRIPTION_LENGTH = 4;

export interface AccessoryDraftApplied {
  draft: IAccessoryDraft;
  /** The accessory type, when the assistant recognised one. The form's own picker still decides. */
  accessoryTypeCode?: string;
}

interface AccessoryDraftAssistantProps {
  disabled?: boolean;
  onApplied: (applied: AccessoryDraftApplied) => void;
}

/**
 * "Dime qué implemento vas a dar de alta" -- and the compatibility sheet
 * fills itself in.
 *
 * **This is where the assistant earns the most per field.** A machine's
 * numbers are on a spec page; an attachment's are a compatibility table --
 * oil flow, working pressure, coupler standard, carrier weight range, one or
 * two lines, case drain -- and the field that costs the most time has no
 * number in it at all: which of the 83 machinery types the thing actually
 * fits. That one is a sentence in a manual ("va en portadores de 18 a 32 t")
 * that somebody has to turn into a list of categories, slowly, once per
 * accessory.
 *
 * Optional in every direction, exactly like its machinery sibling: it can be
 * ignored, it never overwrites an answer already given (see
 * applyAccessoryDraft), and when the assistant is not switched on the block
 * removes itself rather than offering a button that cannot work. It never
 * proposes a price or how many they own.
 */
const AccessoryDraftAssistant: React.FC<AccessoryDraftAssistantProps> = ({ disabled, onApplied }) => {
  const { t, i18n } = useTranslation();
  const dictation = useSpeechDictation(i18n.language);

  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<IClarifyingQuestion[]>();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [identified, setIdentified] = useState<IAccessoryIdentifyResult>();
  const [working, setWorking] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (dictation.transcript) setDescription(dictation.transcript);
  }, [dictation.transcript]);

  useEffect(() => {
    if (dictation.error) message.error(translate(t, TRANSLATIONS.DICTATE_ERROR));
  }, [dictation.error, t]);

  /** Turns one failed call into the right sentence, and decides whether the block survives it. */
  const reportFailure = (reason: 'unavailable' | 'limit' | 'error', error?: string) => {
    if (reason === 'unavailable') {
      setUnavailable(true);
      return;
    }
    if (reason === 'limit') {
      message.warning(translate(t, TRANSLATIONS.MACHINERY_AI_LIMIT_REACHED));
      return;
    }
    if (error) console.error('accessory assistant:', error);
    message.error(translate(t, TRANSLATIONS.MACHINERY_AI_FAILED));
  };

  /**
   * The second call.
   *
   * Unlike the machinery one this runs whether or not a type was recognised:
   * plenty of real attachments have no entry in the taxonomy yet, and the
   * sheet -- flow, pressure, coupler, what it fits -- is worth filling in
   * regardless.
   */
  const buildDraft = async (identify?: IAccessoryIdentifyResult, pairs: IServiceRequestClarification[] = []) => {
    const outcome = await draftAccessory(description.trim(), i18n.language, {
      accessoryTypeCode: identify?.accessoryTypeCode,
      manufacturerBrand: identify?.manufacturerBrand,
      modelName: identify?.modelName,
      answers: pairs,
    });

    if (!outcome.ok) {
      reportFailure(outcome.reason, outcome.error);
      return;
    }

    onApplied({ draft: outcome.value, accessoryTypeCode: identify?.accessoryTypeCode });
    // Back to a plain "try again": the questions have been answered, and
    // asking them a second time would look like nobody listened.
    setQuestions(undefined);
    setDone(true);
  };

  const handleStart = async () => {
    if (description.trim().length < MIN_DESCRIPTION_LENGTH) {
      message.error(translate(t, TRANSLATIONS.ACCESSORY_AI_DESCRIPTION_TOO_SHORT));
      return;
    }

    dictation.stop();
    setWorking(true);
    try {
      const outcome = await identifyAccessory(description.trim(), i18n.language);
      if (!outcome.ok) {
        reportFailure(outcome.reason, outcome.error);
        return;
      }

      const identify = outcome.value;
      setIdentified(identify);

      if (identify.questions.length > 0) {
        setQuestions(identify.questions);
        return;
      }

      await buildDraft(identify);
    } finally {
      setWorking(false);
    }
  };

  const handleAnswered = async () => {
    const pairs: IServiceRequestClarification[] = (questions ?? [])
      .map((question) => ({ question: question.question, answer: answers[question.question] }))
      .filter((pair): pair is IServiceRequestClarification => !!pair.answer);

    setWorking(true);
    try {
      await buildDraft(identified, pairs);
    } finally {
      setWorking(false);
    }
  };

  if (unavailable) return null;

  const hasQuestions = !!questions && questions.length > 0;

  return (
    <AssistantPromptBox
      copy={ACCESSORY_ASSISTANT_COPY}
      description={description}
      onDescriptionChange={setDescription}
      dictation={dictation}
      recognised={
        identified && (identified.accessoryTypeName || identified.manufacturerBrand || identified.modelName)
          ? [identified.accessoryTypeName, identified.manufacturerBrand, identified.modelName].filter(Boolean).join(' · ')
          : undefined
      }
      questions={questions}
      answers={answers}
      onAnswer={(question, option) => setAnswers((previous) => ({ ...previous, [question]: option }))}
      working={working}
      done={done}
      disabled={disabled}
      onSubmit={hasQuestions ? handleAnswered : handleStart}
    />
  );
};

export default AccessoryDraftAssistant;
